import { QRCodeSVG } from "qrcode.react";
import { LANGUAGE_NATIVE_LABELS, type LanguageCode } from "../shared";
import {
  PREVIEW_ROWS,
  PREVIEW_SENTENCES,
  PREVIEW_TOPIC,
  type PreviewRow,
} from "./previewSample";

interface SheetPreviewProps {
  left: LanguageCode;
  right: LanguageCode;
  words: number;
  sentences: number;
}

function Cell({
  side,
  language,
  row,
  index,
}: {
  side: "left" | "right";
  language: LanguageCode;
  row: PreviewRow;
  index: number;
}) {
  return (
    <span
      className={`preview__cell preview__cell--${side} preview__cell--${language}`}
      lang={language}
    >
      <span className="preview__n">{index}</span>
      <span className="preview__body-text">
        <b>{row.text[language]}</b>
        {language === "th" ? <i>{row.pronunciation}</i> : null}
      </span>
    </span>
  );
}

/** Repeated in both halves, so a folded-away side keeps its heading. */
function SectionLabel({ text }: { text: string }) {
  return (
    <p className="preview__row preview__row--section">
      <span className="preview__section">
        <span>{text}</span>
      </span>
      <span className="preview__section">
        <span>{text}</span>
      </span>
    </p>
  );
}

function countLine(remainingWords: number, remainingSentences: number): string {
  const parts: string[] = [];
  if (remainingWords > 0) {
    parts.push(`+ ${remainingWords} more ${remainingWords === 1 ? "word" : "words"}`);
  }
  if (remainingSentences > 0) {
    parts.push(
      `+ ${remainingSentences} more ${remainingSentences === 1 ? "sentence" : "sentences"}`,
    );
  }
  return parts.join(" · ");
}

export function SheetPreview({
  left,
  right,
  words,
  sentences,
}: SheetPreviewProps) {
  const shownWords = PREVIEW_ROWS.slice(0, Math.min(words, PREVIEW_ROWS.length));
  const shownSentences = PREVIEW_SENTENCES.slice(
    0,
    Math.min(sentences, PREVIEW_SENTENCES.length),
  );
  const more = countLine(
    words - shownWords.length,
    sentences - shownSentences.length,
  );

  return (
    <div className="preview-stack" aria-hidden="true">
      <span className="preview-stack__paper preview-stack__paper--back" />
      <span className="preview-stack__paper preview-stack__paper--mid" />

      <article className="preview">
        <header className="preview__top">
          <span className="preview__brand">SprinkleDays</span>
          <span className="preview__day">
            Day <b>1</b>
          </span>
        </header>

        <p className="preview__title">
          <span className="preview__emoji">{PREVIEW_TOPIC.emoji}</span>
          {PREVIEW_TOPIC.title}
        </p>

        <div className="preview__body">
          <div className="preview__fold">
            <span>✂ fold</span>
          </div>

          <div className="preview__row preview__row--heads">
            <span className="preview__head preview__head--left" lang={left}>
              {LANGUAGE_NATIVE_LABELS[left]}
            </span>
            <span className="preview__head preview__head--right" lang={right}>
              {LANGUAGE_NATIVE_LABELS[right]}
            </span>
          </div>

          <SectionLabel text="Words" />

          {shownWords.map((row, index) => (
            <div className="preview__row" key={`word-${index}`}>
              <Cell side="left" language={left} row={row} index={index + 1} />
              <Cell side="right" language={right} row={row} index={index + 1} />
            </div>
          ))}

          {shownSentences.length ? (
            <>
              <SectionLabel text="Sentences" />

              {shownSentences.map((row, index) => (
                <div
                  className="preview__row preview__row--sentence"
                  key={`sentence-${index}`}
                >
                  <Cell side="left" language={left} row={row} index={index + 1} />
                  <Cell side="right" language={right} row={row} index={index + 1} />
                </div>
              ))}
            </>
          ) : null}
        </div>

        {more ? <p className="preview__more">{more}</p> : null}

        <footer className="preview__foot">
          <span className="preview__done">
            <span className="preview__box" />
            Done
          </span>
          <span className="preview__qr">
            <QRCodeSVG
              value="https://sprinkledays.app/day/1"
              size={38}
              level="L"
              marginSize={0}
              bgColor="#ffffff"
              fgColor="#17150f"
            />
            Scan to tick off
          </span>
        </footer>
      </article>
    </div>
  );
}
